import {
  MeshPhysicalMaterial,
  TextureLoader,
  Color,
} from "three";
import { ralToHex } from "./RalToHex";
import {
  configureColorMap,
  configureNormalMap,
  syncNormalMapFromColorMap,
} from "./materialTexture";

const materialCache = {};

export function getMaterialFromFormData(materialFormData, onMaterialReady) {
  const data = materialFormData.colorType ? materialFormData : materialFormData.color || materialFormData;
  const cacheKey = JSON.stringify(data);
  if (materialCache[cacheKey]) {
    return materialCache[cacheKey];
  }

  let colorHex;
  if (data.colorType === "RAL") colorHex = ralToHex(data.ralColorId);
  else if (data.colorType === "HEX") colorHex = "#" + data.color.replace(/^#/, "");
  else colorHex = "#ffffff";

  const normalIntensityX = Number(data.normalIntensityX ?? data.scaleX ?? 1) || 1;
  const normalIntensityY = Number(data.normalIntensityY ?? data.scaleY ?? 1) || 1;
  const normalScaleX = Number(data.normalScaleX ?? 1) || 1;
  const normalScaleY = Number(data.normalScaleY ?? 1) || 1;

  const mat = new MeshPhysicalMaterial({
    color: new Color(colorHex),
    roughness: data.roughness,
    metalness: data.metalness,
    clearcoat: data.clearcoat,
    clearcoatRoughness: data.clearcoatRoughness,
    reflectivity: data.reflectivity,
  });
  mat.userData.normalUvScale = { x: normalScaleX, y: normalScaleY };

  if (data.opacity !== undefined) {
    mat.opacity = data.opacity;
    mat.transparent = data.opacity < 1;
  }

  const loader = new TextureLoader();

  let pendingLoads = 0;
  const finishLoad = () => {
    pendingLoads = Math.max(0, pendingLoads - 1);
    if (pendingLoads === 0) onMaterialReady?.();
  };
  const startLoad = () => {
    pendingLoads += 1;
  };

  const loadTexture = (url, configure, assign) => {
    startLoad();
    const texture = loader.load(
      url,
      (tex) => {
        configure(tex);
        assign(tex);
        syncNormalMapFromColorMap(mat);
        finishLoad();
      },
      undefined,
      () => finishLoad(),
    );
    configure(texture);
    assign(texture);
  };

  if (data.colorType === "TEXTURE" && data.colorMapUrl) {
    mat.color = new Color("#" + data.color.replace(/^#/, ""));
    loadTexture(data.colorMapUrl, configureColorMap, (tex) => {
      mat.map = tex;
    });
  }

  if (data.normalMapFile && data.normalMapFile instanceof File) {
    loadTexture(URL.createObjectURL(data.normalMapFile), configureNormalMap, (tex) => {
      mat.normalMap = tex;
    });
  } else if (data.normalMapUrl) {
    loadTexture(data.normalMapUrl, configureNormalMap, (tex) => {
      mat.normalMap = tex;
    });
  }

  if (data.normalMapFile || data.normalMapUrl) {
    mat.normalScale.set(normalIntensityX, normalIntensityY);
  }

  syncNormalMapFromColorMap(mat);

  if (pendingLoads === 0) onMaterialReady?.();

  materialCache[cacheKey] = mat;
  return mat;
}
